import type { Edges, MapNode, NodeId, NodeMap, PipelineStage, SearchScope, TagId, TagMap } from './types';
import type { SearchDocKind } from './location';
import { pipelineStageOf } from './docs';
import { tagDescendants } from './tags';

/**
 * Search, the tag filter and the pipeline-stage filter — the three things that highlight nodes.
 *
 * The behaviour worth preserving is not "typing filters a list". It is the reveal/highlight
 * contract: a hit is highlighted, every non-hit is dimmed, and the ANCESTORS of every hit
 * are force-opened so the hit is actually on screen — without touching the real `collapsed`
 * flags, so clearing the filter packs the tree back exactly as it was. That last half is the
 * part a port breaks.
 */

/** Everything the three filters need, gathered so callers pass one object rather than nine. */
export interface FilterState {
  nodes: NodeMap;
  edges: Edges;
  tags: TagMap;
  tagEdges: Record<TagId, TagId>;
  /** Already lowercased and trimmed, as `applySearch` does before storing it. */
  searchQuery: string;
  searchScope: SearchScope;
  /**
   * Nodes whose documentation TEXT contains the query (plan 2.2) — the owner-node ids from
   * `GET /api/search/docs`, arriving a debounce behind the keystrokes. Empty until (and
   * unless) the content scope or a doc-kind filter asks for it.
   */
  contentHits: ReadonlySet<NodeId>;
  /**
   * Narrow the search to ONE documentation kind (`''` = off). While set, the text scopes
   * are out of play: a hit is a content/title hit among documents of that kind, nothing
   * else. Transient — never persisted, exactly the property the old presence facets had.
   */
  searchDocKind: SearchDocKind | '';
  /** The selected tag filter — several at once since plan 2.3. Empty = no tag filter. */
  activeTags: ReadonlySet<TagId>;
  /** The pipeline-stage filter (V0.38 — the field keeps its historical name). `''`/null = off. */
  activeDocStatus: PipelineStage | '' | null;
  /** Node ids cited by a chatbot answer. Transient; highlighted like a search hit. */
  chatHighlight?: ReadonlySet<NodeId>;
  /** The eye assignment filter's node set (V0.42) — nodes carrying my assigned docs/nodes.
   *  The FIFTH highlight layer: revealed and highlighted like the chat citations. */
  assignHighlight?: ReadonlySet<NodeId>;
  /** Permission-hidden nodes are never hits and never counted. */
  isPermHidden: (id: NodeId) => boolean;
  /** Invisible tags are only searchable to an editor actually in edit mode. */
  editing: boolean;
  canEdit: boolean;
}

/**
 * Is a search running?
 *
 * Typed text is the whole answer now (plan 2.2). The presence facets that used to make an
 * empty query "active" are gone; the doc-kind filter narrows WHAT a query matches, it is
 * not a match by itself.
 */
export function searchActive(s: Pick<FilterState, 'searchQuery' | 'searchScope'>): boolean {
  return !!s.searchQuery;
}

/**
 * Does this node match the current search?
 *
 * With a doc KIND selected the content hits are the only authority — the text scopes are
 * out of play (the panel greys them to say so), and the server already restricted the hits
 * to that kind.
 *
 * The tag scope skips invisible tags unless the user is an editor IN edit mode — otherwise
 * searching would reveal, by its results, that a hidden tag exists.
 */
export function nodeMatchesSearch(n: MapNode, s: FilterState): boolean {
  if (!s.searchQuery) return false;
  if (s.searchDocKind) return s.contentHits.has(n.id);

  if (s.searchScope.name && (n.label ?? '').toLowerCase().includes(s.searchQuery)) return true;
  if (s.searchScope.desc && (n.desc ?? '').toLowerCase().includes(s.searchQuery)) return true;
  if (s.searchScope.tags) {
    const showInvisible = s.editing && s.canEdit;
    const hit = (n.tags ?? []).some((id) => {
      const t = s.tags[id];
      if (!t) return false;
      if (t.invisible && !showInvisible) return false;
      return t.name.toLowerCase().includes(s.searchQuery);
    });
    if (hit) return true;
  }
  if (s.searchScope.content && s.contentHits.has(n.id)) return true;
  return false;
}

/**
 * Is this node a hit for the tag filter — ANY selected tag, or any of their sub-tags?
 *
 * Multi-tag is OR ("any-of") on purpose (plan 2.3): selecting two tags lights every node
 * carrying either, which is what "combine the filters" means to somebody narrowing a map.
 */
export function nodeHasActiveTag(n: MapNode, s: FilterState): boolean {
  if (!s.activeTags.size) return false;
  const wanted = new Set<TagId>();
  for (const t of s.activeTags) {
    wanted.add(t);
    for (const d of tagDescendants(s.tagEdges, t)) wanted.add(d);
  }
  return (n.tags ?? []).some((t) => wanted.has(t));
}

/**
 * Is this node a hit for the selected pipeline-stage filter?
 *
 * `pipelineStageOf` answers `undefined` for a node whose stage this viewer may not see (the
 * field is prompt-scoped on `/api/map`), so such a node is never a hit — and the filter UI
 * only offers itself where the data exists (AI-doc mode) in the first place.
 */
export function nodeHasActivePipelineStage(n: MapNode, s: FilterState): boolean {
  return !!s.activeDocStatus && pipelineStageOf(n) === s.activeDocStatus;
}

/**
 * The ids to treat as expanded so every current hit is visible.
 *
 * Transient by construction: this set is recomputed from the active filters every time and
 * never persisted, so the real `collapsed` flags survive a filter untouched and the tree
 * re-packs exactly when it is cleared.
 *
 * The root is never a hit source — it is always visible, so opening its ancestors is a
 * no-op, and excluding it matches the vanilla behaviour exactly.
 */
export function computeForceOpen(s: FilterState): Set<NodeId> {
  const set = new Set<NodeId>();
  const openAncestors = (id: NodeId) => {
    let cur = s.edges[id];
    let guard = 0;
    while (cur !== undefined && guard++ < 10_000) {
      set.add(cur);
      cur = s.edges[cur];
    }
  };

  const eachEligible = (predicate: (n: MapNode) => boolean) => {
    for (const id in s.nodes) {
      const n = s.nodes[id];
      if (n.root || s.isPermHidden(id)) continue;
      if (predicate(n)) openAncestors(id);
    }
  };

  if (searchActive(s)) eachEligible((n) => nodeMatchesSearch(n, s));
  if (s.activeTags.size) eachEligible((n) => nodeHasActiveTag(n, s));
  if (s.activeDocStatus) eachEligible((n) => nodeHasActivePipelineStage(n, s));

  // Chat citations are a set of ids, not a predicate, and are NOT filtered by permHidden —
  // matching the vanilla behaviour. It is safe because retrieval only ever cites documents
  // the asking user was allowed to retrieve.
  if (s.chatHighlight?.size) {
    for (const id of s.chatHighlight) if (s.nodes[id] && !s.nodes[id].root) openAncestors(id);
  }

  // The eye assignment layer (V0.42): reveal the branches down to every node carrying one of
  // my assignments, so a collapsed ancestor cannot hide it — the same id-set reveal as chat.
  if (s.assignHighlight?.size) {
    for (const id of s.assignHighlight) if (s.nodes[id] && !s.nodes[id].root) openAncestors(id);
  }

  return set;
}

/**
 * Every node the active filter is pointing at — what the camera frames (V0.46).
 *
 * SEARCH AND TAGS, and deliberately not the pipeline-stage filter. Those two are a question
 * asked of the map ("where is this?"), so answering with the camera is the whole point; the
 * stage filter is a lens laid over what you are already looking at, and `48-heatmap`'s "never
 * pans the map" is the assertion that says so out loud.
 *
 * The two are OR'd for the same reason they are OR'd everywhere else here: with a tag selected
 * AND a query typed, a reader looking for "either" would otherwise be shown a frame that
 * excluded half of what is lit up.
 *
 * Distinct from `countSearchMatches` below, which is the search alone — its caller passes an
 * empty tag set precisely so the counter beside the box counts what the box matched.
 */
export function filterMatchIds(s: FilterState): NodeId[] {
  const searching = searchActive(s);
  const tagging = s.activeTags.size > 0;
  if (!searching && !tagging) return [];
  const out: NodeId[] = [];
  for (const id in s.nodes) {
    const n = s.nodes[id];
    if (n.root || s.isPermHidden(id)) continue;
    if ((searching && nodeMatchesSearch(n, s)) || (tagging && nodeHasActiveTag(n, s))) out.push(id);
  }
  return out;
}

/**
 * Every node the SEARCH matched — the counter beside the box, and the node rows in the
 * results dropdown (V0.46), which are the same answer shown rather than totalled.
 *
 * The tag filter is not in it, deliberately: the counter has always reported search hits
 * alone, and its caller passes an empty tag set to say so. `filterMatchIds` above is the one
 * that folds both, because the CAMERA frames whatever is lit up.
 *
 * The root is excluded, as it always was — it is on screen whatever happens, so listing it
 * as somewhere to go is noise.
 */
export function searchMatchIds(s: FilterState): NodeId[] {
  if (!searchActive(s)) return [];
  const out: NodeId[] = [];
  for (const id in s.nodes) {
    if (s.nodes[id].root || s.isPermHidden(id)) continue;
    if (nodeMatchesSearch(s.nodes[id], s)) out.push(id);
  }
  return out;
}

/** How many nodes match — the "12 matches" counter beside the search box. */
export const countSearchMatches = (s: FilterState): number => searchMatchIds(s).length;
